import { ElementType } from '@/modules/elements/models/Element'
import EmbedLinkMenu from '@/modules/global/components/EmbedLinkMenu.vue'
import { ChainedCommands, Editor, Node } from '@tiptap/core'
import { VueRenderer } from '@tiptap/vue-2'
import { Fragment, Node as ProseMirrorNode } from 'prosemirror-model'
import { Plugin, PluginKey } from 'prosemirror-state'
import tippy from 'tippy.js'
import { EmbedLinkMatch, findEmbedLink } from './embed-link-parser'
import { bookmarkAttrsFromPreview } from './tiptap-bookmark'
import { sanitizeEmbedHtml } from './sanitize-embed-html'
import { embedMenuOptions, type EditorContext, type EmbedMenuOption } from './tiptap-editor-context'
import ApiClient from '@/util/ApiClient'
import type { AddElementData } from './tiptap-commands'
import { claimNetworkError } from '@/util/api-error-snackbar'
import { EmbedType } from '@/modules/elements/models/Embed'
import { checkEmbeddable, notifyEmbedAddedAsBookmark, type EmbeddabilityReason } from './embed-embeddability'
import {
  createEmbedCard,
  EMBED_CARD_CLASS,
  EMBED_WRAPPER_CLASS,
  hydrateEmbedCard,
  isCardLoading,
  isClickToLoadEnabled,
  isEmbedLoaded,
  loadEmbedBehindCard,
  markEmbedLoaded,
  srcEmbedKey,
  toSafeEmbedSrc
} from './embed-click-to-load'
import { createEmbedPrintCard, hydratePrintCards, isEmbedPrintCardEnabled } from './embed-print-card'

export interface IframeOptions {
  /** Read when a link is pasted and when the menu choice is made, so a pooled editor uses its current host's */
  ctx: EditorContext | null
  allowFullscreen: boolean
  HTMLAttributes: {
    [key: string]: any
  }
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    iframe: {
      /**
       * Add an iframe
       */
      setIframe: (options: { src: string }) => ReturnType
    }
  }
}

export default Node.create({
  name: 'iframe',
  group: 'block',

  addOptions() {
    return <IframeOptions>{
      ctx: null,
      allowFullscreen: true,
      HTMLAttributes: {
        class: 'iframe-wrapper'
      }
    }
  },

  addAttributes() {
    return {
      src: {
        default: null
      },
      frameborder: {
        default: 0
      },
      allowfullscreen: {
        default: this.options.allowFullscreen,
        parseHTML: () => {
          return {
            allowfullscreen: this.options.allowFullscreen
          }
        }
      }
    }
  },

  parseHTML() {
    return [
      {
        tag: 'iframe'
      }
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', this.options.HTMLAttributes, ['iframe', HTMLAttributes]]
  },

  // Saved HTML still comes from renderHTML; only what the editor shows changes: a click-to-load card until clicked
  addNodeView() {
    const wrapperClass = this.options.HTMLAttributes.class
    return ({ node }) => {
      let current = node
      // A player waiting to replace its card; stopped when this view renders again or is destroyed
      let cancelWait: (() => void) | null = null
      const dom = document.createElement('div')
      dom.className = wrapperClass
      // The card is a button and must be its own tab stop, not part of the editing host (ProseMirror also sets this
      // on leaf node views; set here so it does not depend on that)
      dom.setAttribute('contenteditable', 'false')

      const stopWaiting = () => {
        cancelWait?.()
        cancelWait = null
      }

      const render = () => {
        stopWaiting()
        const src = current.attrs.src as string | null
        const safeSrc = toSafeEmbedSrc(src)
        const clickToLoad = isClickToLoadEnabled()
        dom.textContent = ''
        dom.classList.toggle(EMBED_WRAPPER_CLASS, clickToLoad)
        // The PDF export shows where the embed leads, and loads no player (canvas load 1.6)
        if (isEmbedPrintCardEnabled()) {
          dom.appendChild(createEmbedPrintCard(document, src))
          hydratePrintCards(dom)
          return
        }
        if (!clickToLoad) {
          dom.appendChild(liveIframe(current))
          return
        }
        const card = createEmbedCard(document, src)
        dom.appendChild(card)
        hydrateEmbedCard(card)
        // Loaded earlier this session (e.g. in the static cell this editor replaced): load again behind the card
        if (safeSrc && isEmbedLoaded(srcEmbedKey(safeSrc))) cancelWait = loadEmbedBehindCard(card, safeSrc, false)
      }

      const onClick = (event: MouseEvent) => {
        const card = (event.target as Element | null)?.closest?.(`.${EMBED_CARD_CLASS}`) as HTMLButtonElement | null
        const src = card?.getAttribute('data-embed-src')
        if (!card || card.disabled || isCardLoading(card) || !src) return
        event.preventDefault()
        markEmbedLoaded(srcEmbedKey(src))
        cancelWait = loadEmbedBehindCard(card, src, true)
      }

      render()
      dom.addEventListener('click', onClick)
      return {
        dom,
        update: updated => {
          if (updated.type !== current.type) return false
          const srcChanged = updated.attrs.src !== current.attrs.src
          current = updated
          if (srcChanged) render()
          return true
        },
        // The card is a button: ProseMirror must not turn a press on it into a selection
        stopEvent: event => !!(event.target as Element | null)?.closest?.(`.${EMBED_CARD_CLASS}`),
        ignoreMutation: () => true,
        destroy: () => {
          stopWaiting()
          dom.removeEventListener('click', onClick)
        }
      }
    }
  },

  addCommands() {
    return {
      setIframe:
        (options: { src: string }) =>
        ({ tr, dispatch }) => {
          const { selection } = tr
          const node = this.type.create(options)

          if (dispatch) {
            tr.replaceRangeWith(selection.from, selection.to, node)
          }

          return true
        }
    }
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('embedLinkReplacerPasteRule'),
        props: {
          handlePaste: (_view, event, slice) => {
            const { ctx } = this.options
            // Only hosts that can show an embed or bookmark offer the menu: text blocks (as elements) and table cells (as nodes)
            const options = embedMenuOptions(ctx)
            const embedLink = options ? findEmbedLinkInChildren(slice.content) : null
            if (ctx && options && embedLink) {
              const parentElement = (event.target as any).parentElement
              setTimeout(() => {
                openEmbedLinkMenu(ctx, parentElement, this.editor, embedLink, options)
              }, 10)
            }
            return false
          }
        }
      })
    ]
  }
})

/** What renderHTML puts in the wrapper, for editors without click-to-load or print cards (flag off). */
function liveIframe(node: ProseMirrorNode): HTMLIFrameElement {
  const iframe = document.createElement('iframe')
  Object.entries(node.attrs).forEach(([name, value]) => {
    if (value === null || value === undefined) return
    if (name === 'src') {
      // As in createEmbedCard, the checked URL is what loads: a `javascript:` src would run in the app's origin
      const safeSrc = toSafeEmbedSrc(String(value))
      if (safeSrc) iframe.setAttribute('src', safeSrc)
      return
    }
    iframe.setAttribute(name, typeof value === 'object' ? 'true' : String(value))
  })
  return iframe
}

function findEmbedLinkInChildren(childrenContainer: ProseMirrorNode | Fragment): EmbedLinkMatch | null {
  let foundLink = null
  childrenContainer.forEach(n => {
    const embedLink = findEmbededLink(n)
    if (embedLink) {
      foundLink = embedLink
    }
  })
  return foundLink
}

function findEmbededLink(node: ProseMirrorNode): EmbedLinkMatch | null {
  if (node.isText) {
    return findEmbedLink(node.text!!)
  } else {
    return findEmbedLinkInChildren(node)
  }
}

function openEmbedLinkMenu(
  ctx: EditorContext,
  parentElement: HTMLElement,
  editor: Editor,
  embedLink: EmbedLinkMatch,
  options: EmbedMenuOption[]
) {
  const component = new VueRenderer(EmbedLinkMenu, { parent: ctx.parent, propsData: { options } })

  const popup = tippy('body', {
    getReferenceClientRect: () => (parentElement.lastChild as any)?.getBoundingClientRect(),
    appendTo: 'parent',
    content: component.element,
    aria: {
      content: 'auto',
      expanded: 'auto'
    },
    showOnCreate: true,
    interactive: true,
    hideOnClick: true,
    theme: 'none',
    trigger: 'manual',
    placement: 'bottom-end'
  })

  const ref = component.ref as any
  ref.onSelect = (option: string) => {
    if (popup[0]) {
      popup[0].hide()
    }
    handleSelectEmbedMenu(ctx, option, editor, embedLink)
  }
}

export function handleSelectEmbedMenu(ctx: EditorContext, option: string, editor: Editor, embedLink: EmbedLinkMatch) {
  const { replaceElementHandler } = ctx
  if (option !== 'bookmark' && option !== 'embed') {
    editor.chain().focus().insertContent(' ').run()
  } else {
    const lastMatchFrom = findLastMatch(editor.state.doc, embedLink.original)
    const lastMatchTo = lastMatchFrom + embedLink.original.length
    if (lastMatchFrom >= 0) {
      if (!replaceElementHandler) {
        // A table cell: the embed or bookmark becomes a node in the cell itself
        if (option === 'embed') insertCellEmbed(ctx, editor, embedLink)
        else insertCellBookmark(ctx, editor, embedLink)
      } else {
        if (option === 'embed') {
          // The link is looked up and checked first: by then the editor may be gone or the text edited, so the link
          // is found again, and nothing is replaced when it is no longer there. A pooled editor may also have moved on
          // to another block (same editor, same ctx): its token says so
          const token = ctx.activationToken
          pastedEmbedElement(embedLink)
            .then(({ element, bookmarkReason }) => {
              if (editor.isDestroyed || ctx.activationToken !== token) return
              const from = findLastMatch(editor.state.doc, embedLink.original)
              if (from < 0) return
              replaceElementHandler(element)
              editor
                .chain()
                .focus()
                .deleteRange({ from, to: from + embedLink.original.length })
                .run()
              // Only once the bookmark is really there: a stale lookup inserts nothing and says nothing
              if (bookmarkReason) notifyEmbedAddedAsBookmark(embedLink.embed, bookmarkReason)
            })
            .catch(error => console.warn('[tiptap-iframe] could not replace the pasted link', error))
        } else {
          replaceElementHandler({ type: ElementType.linkbookmark, embedUrl: embedLink.original })
          editor.chain().focus().deleteRange({ from: lastMatchFrom, to: lastMatchTo }).run()
        }
      }
    }
  }
}

/** Links that are no player themselves: iframe.ly is asked for one, and without it the page is framed as it is */
function needsPlayer(embedLink: EmbedLinkMatch): boolean {
  return embedLink.type === EmbedType.GENERIC || embedLink.type === EmbedType.IFRAME
}

/** The element choosing Embed puts in the block's place, and why it is a bookmark when the link cannot be framed */
type PastedEmbed = { element: AddElementData; bookmarkReason?: EmbeddabilityReason }

/**
 * What choosing Embed for a link pasted in a text block puts in the block's place: a known provider's player, else
 * iframe.ly's embed, else the page framed as it is - or, when its site refuses to be framed or it cannot be reached,
 * a bookmark. The caller says why, once the bookmark is in.
 */
export async function pastedEmbedElement(embedLink: EmbedLinkMatch): Promise<PastedEmbed> {
  if (!needsPlayer(embedLink)) {
    return { element: { type: ElementType.embed, embedType: embedLink.type, embedUrl: embedLink.embed } }
  }
  const analyzed = await ApiClient.post(`/utilities/analyze-embed-url`, { url: embedLink.original })
    .then(response => response.data)
    .catch(error => {
      // The link is checked and framed without it: no generic network error
      claimNetworkError(error)
      return undefined
    })
  if (analyzed && !analyzed.error && analyzed.html) {
    return { element: { type: ElementType.embed, embedType: EmbedType.IFRAMELY, embedHtml: analyzed.html as string } }
  }
  const check = await checkEmbeddable(embedLink.embed)
  if (!check.embeddable) {
    return { element: { type: ElementType.linkbookmark, embedUrl: embedLink.original }, bookmarkReason: check.reason }
  }
  return { element: { type: ElementType.embed, embedType: embedLink.type, embedUrl: embedLink.embed } }
}

/** A slow lookup gives up and the cell gets the host card or the bare link, rather than keeping the raw URL waiting */
const PREVIEW_LOOKUP_TIMEOUT = 5000

/** The iframe.ly response for a link, or undefined when it has none: a failed lookup must not block the insert */
function lookUpPreview(url: string): Promise<any> {
  return ApiClient.post(`/utilities/analyze-embed-url`, { url }, { timeout: PREVIEW_LOOKUP_TIMEOUT })
    .then(response => response.data)
    .catch(error => {
      // The cell gets the host card or the bare link instead: no generic network error
      claimNetworkError(error)
      return undefined
    })
}

/**
 * Replaces the pasted link once `lookup` is in. The editor may be a pooled one that moved on to another cell, or the
 * link may have been edited, while the lookup ran: then nothing is inserted.
 */
function replaceLinkAfter<T>(
  ctx: EditorContext,
  editor: Editor,
  embedLink: EmbedLinkMatch,
  lookup: Promise<T>,
  insert: (chain: ChainedCommands, result: T) => ChainedCommands
) {
  const token = ctx.activationToken
  lookup
    .then(result => {
      if (editor.isDestroyed || ctx.activationToken !== token) return
      const from = findLastMatch(editor.state.doc, embedLink.original)
      if (from < 0) return
      insert(
        editor
          .chain()
          .focus()
          .deleteRange({ from, to: from + embedLink.original.length }),
        result
      ).run()
    })
    // A failed insert leaves the pasted link as text; it must not surface as an unhandled rejection
    .catch(error => console.warn('[tiptap-iframe] could not replace the pasted link', error))
}

/** A table cell's Bookmark: a card built from the link's preview, or from its host when there is none */
export function insertCellBookmark(ctx: EditorContext, editor: Editor, embedLink: EmbedLinkMatch) {
  if (!bookmarkAttrsFromPreview(embedLink.original, undefined)) return
  replaceLinkAfter(ctx, editor, embedLink, lookUpPreview(embedLink.original), (chain, preview) => {
    const attrs = bookmarkAttrsFromPreview(embedLink.original, preview)
    return attrs ? chain.setBookmark(attrs) : chain
  })
}

/**
 * A table cell's Embed: an iframe in the cell. A known provider has its embed URL; any other link is no player
 * itself (most sites refuse to be framed), so iframe.ly is asked for one, as for an embed element, and the link
 * is framed as it is only when there is none.
 */
export function insertCellEmbed(ctx: EditorContext, editor: Editor, embedLink: EmbedLinkMatch) {
  const cellEmbed: Promise<CellEmbed> = needsPlayer(embedLink)
    ? lookUpPreview(embedLink.original).then(async preview => {
        const player = playerSrc(preview?.html)
        if (player) return { src: player }
        // The page framed as it is: a site that refuses it, or a link that cannot be reached, becomes a bookmark
        const check = await checkEmbeddable(embedLink.embed)
        return check.embeddable ? { src: embedLink.embed } : { preview, reason: check.reason }
      })
    : Promise.resolve({ src: embedLink.embed })
  replaceLinkAfter(ctx, editor, embedLink, cellEmbed, (chain, embed) => {
    if ('src' in embed) return chain.setIframe({ src: embed.src })
    const attrs = bookmarkAttrsFromPreview(embedLink.original, embed.preview)
    if (!attrs) return chain
    notifyEmbedAddedAsBookmark(embedLink.embed, embed.reason)
    return chain.setBookmark(attrs)
  })
}

/** A cell's Embed once looked up: the player to frame, or the bookmark it becomes when the link cannot be framed */
type CellEmbed = { src: string } | { preview: unknown; reason: EmbeddabilityReason }

/** The https iframe in iframe.ly's embed HTML; sanitized first, as the HTML comes from a third party */
function playerSrc(html: unknown): string | null {
  if (typeof html !== 'string') return null
  return sanitizeEmbedHtml(html).querySelector('iframe[src]')?.getAttribute('src') ?? null
}

function findLastMatch(node: ProseMirrorNode, searchTerm: string): number {
  if (node.isText || node.isTextblock) {
    const index = node.textContent ? node.textContent.lastIndexOf(searchTerm) : -1
    return index
  } else {
    const results: number[] = []
    node.content.forEach(node => {
      results.push(findLastMatch(node, searchTerm))
    })
    for (let i = results.length - 1; i >= 0; i--) {
      const result = results[i]
      if (result >= 0) {
        let sizeBeforeMatch = 0
        for (let j = 0; j < i; j++) {
          sizeBeforeMatch += node.content.child(j).nodeSize
        }
        // If result was found in this tree, add all the sizes of the nodes before, plus one for the node start token
        return sizeBeforeMatch + result + 1
      }
    }

    return -1
  }
}
